// Stimulus controller for the VCP model book chart panel.
//
// Bound to the outer container that wraps both the setup list (left)
// and the chart (right). Two responsibilities:
//
//   1. On connect, load the initial setup's chart data + overlays.
//   2. When a setup row is clicked, re-fetch chart data and re-render
//      overlays for the newly-selected setup — without a full page reload.
//
// Overlays attached to the chart series:
//   - VcpBaseOverlay primitive: the base — translucent band over the period
//     (cloned from PowerTrendBackground), its ceiling at base_high, and the
//     base's own measurements named on it
//   - VcpMarkupOverlay primitive: the O'Neil bands (target / buy zone /
//     loss), the contraction polyline with leg depths, and the % to/from
//     pivot readout — all derived from the geometry, never the outcome
//   - VcpRunupOverlay primitive: the pre-base run-up shaded with its excess
//     return over the market, amber when the detector applied the penalty
//   - VcpAnnotationMarksOverlay primitive: an accepted annotation's marks,
//     drawn DASHED because they are asserted by a model rather than measured
//   - createPriceLine: horizontal pivot price line
//   - createSeriesMarkers: peak-rating-day and resolution-day markers
//
// Attached to the VOLUME series (optional — a member can turn volume off):
//   - VcpEdgeVolumeOverlay primitive: the right-edge volume window shaded
//     with its 10/50 ratio, green inside the calibrated band
//
// Handed to the chart controller rather than attached here:
//   - updateRsLine / addSetupBarMarkers, which own their own handles
//
// The header shows the base stage/count and, for a resolved breakout, the
// measured-move caption. Both arrive as strings from the server (overlay
// .base_stage / .caption); the caption is absent from the labelling bench's
// payload on purpose, and this controller must never derive one itself.
// overlay.annotation is the same kind of field: outcome-gated server-side
// (its body narrates the resolution), so its absence is the gate here too.

import { Controller } from "@hotwired/stimulus"
import { createSeriesMarkers } from "lightweight-charts"
import { VcpBaseOverlay } from "../utils/vcp_base_overlay"
import { VcpMarkupOverlay } from "../utils/vcp_markup_overlay"
import { VcpAnnotationMarksOverlay } from "../utils/vcp_annotation_marks_overlay"
import { VcpRunupOverlay, VcpEdgeVolumeOverlay } from "../utils/vcp_context_overlay"

const CHART_READY_POLL_MS = 50
const CHART_READY_MAX_WAIT_MS = 5000
const CHART_READY_SLOW_POLL_MS = 1000
const CHART_READY_SLOW_MAX_WAIT_MS = 10 * 60 * 1000

const END_REASON_COLORS = {
  breakout: "#4caf50",
  failed: "#e53935",
  stale: "#9e9e9e",
  open: "#1976d2"
}

const END_REASON_BADGE = {
  breakout: "bg-success",
  failed: "bg-danger",
  stale: "bg-secondary",
  open: "bg-info"
}

export default class extends Controller {
  static values = {
    setupId: Number,
    chartDataUrl: String
  }

  async connect() {
    this.basePrimitive = null
    this.markupPrimitive = null
    this.annotationPrimitive = null
    this.runupPrimitive = null
    this.edgeVolumePrimitive = null
    this.pivotLine = null
    this.markersPrimitive = null
    this.currentRow = this.element.querySelector("tr.setup-row.table-active")

    // Keyboard navigation: arrows/space move the selection, x/Enter toggles
    // the watchlist for the selected row (mirrors the relative-strength and
    // IPO list pages, which drive lightweight-chart the same way).
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)

    // The member's note form (model book only) tells us when a setup gains or
    // loses a note, so the row's "N" badge follows without a reload.
    this.boundHandleNoteChanged = this.handleNoteChanged.bind(this)
    this.element.addEventListener("model-book-note:changed", this.boundHandleNoteChanged)

    if (!this.chartDataUrlValue) return
    await this.loadSetup(this.chartDataUrlValue)
  }

  disconnect() {
    this.detachOverlays()
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
      this.boundHandleKeydown = null
    }
    if (this.boundHandleNoteChanged) {
      this.element.removeEventListener("model-book-note:changed", this.boundHandleNoteChanged)
      this.boundHandleNoteChanged = null
    }
  }

  // Add or remove the "N" badge beside a row's ticker after the member saved
  // or deleted their note. The badge is private to them; nothing else on the
  // row changes.
  handleNoteChanged(event) {
    const { setupId, noted, tags } = event.detail || {}
    const row = setupId && this.element.querySelector(`tr.setup-row[data-setup-id="${setupId}"]`)
    if (!row) return

    const cell = row.querySelector("td:nth-child(2)") || row.querySelector("td")
    let badge = row.querySelector(".setup-noted-badge")
    if (!noted) {
      badge?.remove()
      return
    }
    if (!badge) {
      badge = document.createElement("span")
      badge.className = "badge text-bg-warning ms-1 setup-noted-badge"
      badge.textContent = "N"
      cell?.appendChild(badge)
    }
    badge.title = tags?.length ? `Your note · tags: ${tags.join(", ")}` : "Your note"
  }

  // Row click handler.
  async selectSetup(event) {
    await this.selectRow(event.currentTarget)
  }

  // Select a row (from click or keyboard), highlight it, scroll it into view,
  // and load its chart + overlays.
  async selectRow(row) {
    if (!row) return

    const id = Number(row.dataset.setupId)
    const url = row.dataset.vcpAnnotationsUrlParam
    if (!id || !url) return

    this.element.querySelectorAll("tr.setup-row").forEach(tr => {
      const isSelected = Number(tr.dataset.setupId) === id
      tr.classList.toggle("table-active", isSelected)
      tr.classList.toggle("fw-bold", isSelected)
      tr.setAttribute("aria-pressed", isSelected ? "true" : "false")
    })

    this.currentRow = row
    row.scrollIntoView({ block: "nearest" })

    await this.loadSetup(url)
  }

  // Document-level keyboard handler for list navigation. Form controls keep
  // their own keys: a focused BUTTON must still activate on Space/Enter (the
  // ratification page mounts this controller for its chart with no rows).
  handleKeydown(event) {
    if (event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT" ||
        event.target.tagName === "BUTTON" ||
        event.target.isContentEditable) {
      return
    }

    if (event.code === "ArrowDown" || event.code === "ArrowUp" || event.code === "Space") {
      event.preventDefault()
      this.navigateRows(event.code === "ArrowUp" ? -1 : 1)
    } else if (event.key === "x" || event.key === "X" || event.key === "Enter") {
      this.dispatchWatchlistToggle()
    }
  }

  // Move the selection up (direction -1) or down (direction +1) one row.
  navigateRows(direction) {
    const rows = Array.from(this.element.querySelectorAll("tr.setup-row"))
    if (!rows.length) return

    const current = this.currentRow || this.element.querySelector("tr.setup-row.table-active")
    if (!current) {
      this.selectRow(rows[0])
      return
    }

    const nextIndex = rows.indexOf(current) + direction
    if (nextIndex < 0 || nextIndex >= rows.length) return

    this.selectRow(rows[nextIndex])
  }

  // Toggle the watchlist for the currently-selected row (x / Enter key).
  // Handled by the watchlist-selection controller wrapping the page.
  dispatchWatchlistToggle() {
    const current = this.currentRow || this.element.querySelector("tr.setup-row.table-active")
    if (!current) return

    const ticker = current.querySelector("input.watchlist-row-checkbox")?.dataset.ticker
    if (!ticker) return

    document.dispatchEvent(new CustomEvent("watchlist:toggle", {
      detail: { ticker },
      bubbles: true
    }))
  }

  async loadSetup(url) {
    // Wait for the chart BEFORE fetching, as this has always done: there is
    // no point spending a request on a chart that will never be there.
    const chartController = await this.waitForChartController()
    if (!chartController) return

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const payload = await response.json()

      await this.applyPayload(payload)
    } catch (err) {
      console.error("[vcp-annotations] Failed to load setup chart data", err)
    }
  }

  // Public: draw a chart-data payload that somebody else fetched.
  //
  // Study mode reveals a setup's outcome by re-fetching the SAME payload
  // shape with the forward window and the outcome keys present, then handing
  // it here — the redraw path is this one, so a revealed chart cannot drift
  // from a row-click one.
  async applyPayload(payload) {
    const chartController = await this.waitForChartController()
    if (!chartController) return

    this.detachOverlays(chartController)
    this.applyChartData(chartController, payload)
    this.applyOverlays(chartController, payload)
    // Setup-bar entry markers ride the same payload; the chart controller
    // owns the primitive so ticker-driven reloads replace it cleanly.
    if (typeof chartController.addSetupBarMarkers === "function") {
      try { chartController.addSetupBarMarkers(payload.setup_bars) } catch { /* markers are optional */ }
    }
    // The RS line rides the same payload. Called unconditionally even when the
    // key is absent, because updateRsLine clears first: a row click that moves
    // from a stock with a line to one without has to take the old line away,
    // and with it the widened bottom margin it asked the price scale for.
    //
    // In study mode the server builds it from the TRUNCATED bars, so the line
    // stops at the same place the candles do — the reveal re-fetch is what
    // extends it.
    if (typeof chartController.updateRsLine === "function") {
      try { chartController.updateRsLine(payload.rs_line) } catch { /* an overlay is optional, the chart is not */ }
    }
    this.updateHeader(payload.overlay, payload.stock)
    this.updateMemberNote(payload.member_note)
  }

  // Refill the member's private note form for the setup just loaded.
  //
  // TOP LEVEL on the payload, never inside `overlay`: the overlay is
  // member-agnostic and shared with the study bench and the admin pages, and
  // only /setups' endpoint carries this key at all. Every step is
  // guarded because most pages mounting this controller have no note form.
  updateMemberNote(payload) {
    if (!payload) return

    const el = this.element.querySelector('[data-controller~="model-book-note"]')
    if (!el) return

    const controller = this.application.getControllerForElementAndIdentifier(el, "model-book-note")
    if (controller && typeof controller.load === "function") controller.load(payload)
  }

  // Two-phase wait. lightweight-chart builds its chart inside a
  // requestAnimationFrame, which a hidden tab suspends entirely — a
  // background-tab load, or an automation window whose visibilityState is
  // always "hidden", can sit for minutes before that frame runs. Poll fast
  // for the normal case, then slowly for a long time rather than abandoning
  // the setup: giving up used to leave a permanently blank chart beside a
  // fully rendered page.
  async waitForChartController() {
    const fastDeadline = Date.now() + CHART_READY_MAX_WAIT_MS
    const slowDeadline = Date.now() + CHART_READY_SLOW_MAX_WAIT_MS
    while (Date.now() < slowDeadline) {
      if (!this.element.isConnected) return null
      const ctrl = this.application.getControllerForElementAndIdentifier(this.element, "lightweight-chart")
      if (ctrl && ctrl.series && ctrl.chart) return ctrl
      const delay = Date.now() < fastDeadline ? CHART_READY_POLL_MS : CHART_READY_SLOW_POLL_MS
      await new Promise(resolve => setTimeout(resolve, delay))
    }
    console.warn("[vcp-annotations] lightweight-chart controller did not become ready")
    return null
  }

  applyChartData(chartController, payload) {
    const { data, stock } = payload
    if (!data || data.length === 0) return

    chartController.series.setData(data)
    if (chartController.showVolumeValue && chartController.volumeSeries) {
      try {
        const volumeData = data.map(bar => ({
          time: bar.time,
          value: bar.volume || 0,
          color: bar.close >= bar.open ? "rgba(34,139,34,0.4)" : "rgba(178,34,52,0.4)"
        }))
        chartController.volumeSeries.setData(volumeData)
      } catch { /* continue without volume */ }
    }

    if (stock && typeof chartController.updateWatermark === "function") {
      chartController.updateWatermark(stock)
    }

    // Moving averages come from the chart controller's own ma-config value
    // (client-computed from the bars just set), so a page can ask for e.g.
    // 10/20 EMA + 50 SMA on the row element and get them redrawn on every
    // setup change. Pages that set no config get no lines, as before.
    if (typeof chartController.clearMovingAverageLines === "function" &&
        typeof chartController.addConfiguredMovingAverages === "function") {
      try {
        chartController.clearMovingAverageLines()
        chartController.addConfiguredMovingAverages(data)
      } catch { /* MAs are decoration */ }
    }

    chartController.chart.timeScale().fitContent()
    try {
      chartController.chart.priceScale("right").applyOptions({ autoScale: true })
    } catch { /* no-op */ }
  }

  applyOverlays(chartController, payload) {
    const { data, overlay } = payload
    if (!overlay) return

    if (overlay.base_start && overlay.base_end) {
      this.basePrimitive = new VcpBaseOverlay({
        baseStart: overlay.base_start,
        baseEnd: overlay.base_end,
        baseHigh: overlay.base_high,
        baseLength: overlay.base_length,
        baseDepth: overlay.base_depth,
        shape: overlay.shape,
        data: data
      })
      chartController.series.attachPrimitive(this.basePrimitive)
    }

    // Presence is the gate: VcpChartOverlay omits each region's keys when the
    // detector measured nothing, so there is no region to assert.
    if (overlay.prior_runup_from) {
      this.runupPrimitive = new VcpRunupOverlay({
        from: overlay.prior_runup_from,
        to: overlay.prior_runup_to,
        pct: overlay.prior_runup_pct,
        lateStage: overlay.late_stage,
        baseStart: overlay.base_start,
        data: data
      })
      chartController.series.attachPrimitive(this.runupPrimitive)
    }

    // The volume histogram is optional — a member can turn volume off, and then
    // there is no series to shade.
    if (overlay.edge_vol_from && chartController.volumeSeries) {
      this.edgeVolumePrimitive = new VcpEdgeVolumeOverlay({
        from: overlay.edge_vol_from,
        to: overlay.edge_vol_to,
        ratio: overlay.edge_vol_ratio,
        band: overlay.edge_vol_band,
        data: data
      })
      chartController.volumeSeries.attachPrimitive(this.edgeVolumePrimitive)
    }

    if (overlay.pivot_price) {
      this.markupPrimitive = new VcpMarkupOverlay({
        pivotPrice: overlay.pivot_price,
        pivotTime: overlay.pivot_date,
        baseLow: overlay.base_low,
        buyZonePct: overlay.buy_zone_pct,
        contractions: overlay.contractions,
        data: data
      })
      chartController.series.attachPrimitive(this.markupPrimitive)

      this.pivotLine = chartController.series.createPriceLine({
        price: Number(overlay.pivot_price),
        color: "#ff9800",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: "Pivot"
      })
    }

    // Only ever present under reveal_outcome (VcpChartOverlay); the study
    // bench's payload has no such key, so nothing is drawn there.
    if (Array.isArray(overlay.annotation?.shapes) && overlay.annotation.shapes.length) {
      this.annotationPrimitive = new VcpAnnotationMarksOverlay({ shapes: overlay.annotation.shapes })
      chartController.series.attachPrimitive(this.annotationPrimitive)
    }

    const markers = []
    if (overlay.peak_rating_date) {
      markers.push({
        time: overlay.peak_rating_date,
        position: "aboveBar",
        color: "#1976d2",
        shape: "arrowDown",
        text: "Peak"
      })
    }
    if (overlay.resolution_date && END_REASON_COLORS[overlay.end_reason]) {
      markers.push({
        time: overlay.resolution_date,
        position: overlay.end_reason === "breakout" ? "belowBar" : "aboveBar",
        color: END_REASON_COLORS[overlay.end_reason],
        shape: overlay.end_reason === "breakout" ? "arrowUp" : "arrowDown",
        text: overlay.end_reason.charAt(0).toUpperCase() + overlay.end_reason.slice(1)
      })
    }

    // Contraction markers: one pair per detected pullback. Peaks above the
    // bar (orange dot), troughs below (purple dot). The depth percentage is
    // written on the polyline leg by VcpMarkupOverlay, not repeated here.
    if (Array.isArray(overlay.contractions)) {
      overlay.contractions.forEach(c => {
        if (c.peak_date) {
          markers.push({
            time: c.peak_date,
            position: "aboveBar",
            color: "#ff9800",
            shape: "circle"
          })
        }
        if (c.trough_date) {
          markers.push({
            time: c.trough_date,
            position: "belowBar",
            color: "#7e57c2",
            shape: "circle"
          })
        }
      })
    }

    if (markers.length > 0) {
      markers.sort((a, b) => a.time.localeCompare(b.time))
      try {
        this.markersPrimitive = createSeriesMarkers(chartController.series, markers)
      } catch (err) {
        console.warn("[vcp-annotations] Failed to set markers", err)
      }
    }
  }

  // Rewrite the header content in-place on row-click navigation.
  updateHeader(overlay, stock) {
    if (!overlay || !stock) return

    const title = this.element.querySelector(".setup-header-title")
    if (title) title.textContent = `${stock.ticker} — ${stock.name}`

    const meta = this.element.querySelector(".setup-header-meta")
    if (meta) {
      const pivot = Number(overlay.pivot_price).toFixed(2)
      const stage = overlay.base_stage ? ` · Stage ${overlay.base_stage}` : ""
      meta.textContent = `Base ${overlay.base_start} → ${overlay.base_end} · Pivot $${pivot} · Peak on ${overlay.peak_rating_date}${stage}`
    }

    const caption = this.element.querySelector(".setup-header-caption")
    if (caption) {
      caption.textContent = overlay.caption || ""
      caption.classList.toggle("d-none", !overlay.caption)
    }

    const badge = this.element.querySelector(".setup-header-badge")
    if (badge) {
      badge.textContent = overlay.end_reason
      badge.className = `badge setup-header-badge ${END_REASON_BADGE[overlay.end_reason] || "bg-secondary"}`
    }

    this.updateAnnotation(overlay.annotation)
  }

  // Fill (or empty) the annotation block beside the caption. Model-written
  // text is set as textContent, one node per paragraph — never innerHTML.
  updateAnnotation(annotation) {
    const block = this.element.querySelector(".setup-annotation")
    if (!block) return

    block.classList.toggle("d-none", !annotation)
    if (!annotation) return

    const pattern = block.querySelector(".setup-annotation-pattern")
    if (pattern) pattern.textContent = annotation.pattern_label || ""

    const stale = block.querySelector(".setup-annotation-stale")
    if (stale) stale.classList.toggle("d-none", !annotation.stale)

    const title = block.querySelector(".setup-annotation-title")
    if (title) title.textContent = annotation.title || ""

    const body = block.querySelector(".setup-annotation-body")
    if (body) {
      body.replaceChildren()
      String(annotation.body || "").split(/\n\s*\n/).forEach(para => {
        if (!para.trim()) return
        const p = document.createElement("p")
        p.className = "mb-1"
        p.textContent = para.trim()
        body.appendChild(p)
      })
    }

    const caveats = block.querySelector(".setup-annotation-caveats")
    if (caveats) {
      caveats.textContent = annotation.caveats ? `Caveats: ${annotation.caveats}` : ""
      caveats.classList.toggle("d-none", !annotation.caveats)
    }
  }

  detachOverlays(chartControllerArg) {
    const chartController = chartControllerArg || this.findChartController()
    if (!chartController || !chartController.series) return

    if (this.basePrimitive) {
      try { chartController.series.detachPrimitive(this.basePrimitive) } catch { /* no-op */ }
      this.basePrimitive = null
    }
    if (this.markupPrimitive) {
      try { chartController.series.detachPrimitive(this.markupPrimitive) } catch { /* no-op */ }
      this.markupPrimitive = null
    }
    if (this.annotationPrimitive) {
      try { chartController.series.detachPrimitive(this.annotationPrimitive) } catch { /* no-op */ }
      this.annotationPrimitive = null
    }
    if (this.runupPrimitive) {
      try { chartController.series.detachPrimitive(this.runupPrimitive) } catch { /* no-op */ }
      this.runupPrimitive = null
    }
    // On the volume series, not the price one — and the volume series may be
    // gone entirely by now (a member turning volume off rebuilds the chart).
    if (this.edgeVolumePrimitive) {
      try { chartController.volumeSeries?.detachPrimitive(this.edgeVolumePrimitive) } catch { /* no-op */ }
      this.edgeVolumePrimitive = null
    }
    if (this.pivotLine) {
      try { chartController.series.removePriceLine(this.pivotLine) } catch { /* no-op */ }
      this.pivotLine = null
    }
    if (this.markersPrimitive) {
      try { this.markersPrimitive.detach() } catch { /* no-op */ }
      this.markersPrimitive = null
    }
  }

  findChartController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "lightweight-chart")
  }
}
